import type { DefaultTheme } from "styled-components";
import { MEDIA_TIMELINE_POLICY, mediaTimelineFraction } from "./mediaTimelineMath";
import {
  ACTIVE_SEGMENT_FILL_PERCENT,
  SEGMENT_FILL_PERCENT,
  segmentLineOf,
} from "./timelineSegmentPaint";
import { resolveCanvasToken } from "./timelineTickCanvas";
import type { MediaTimelineSegment, MediaTimelineSegmentTone } from "./timelineTypes";

/** No band is sampled, widened or merged; active bands paint above passive siblings. */
export function paintTimelineSegmentCanvas(
  context: CanvasRenderingContext2D,
  segments: readonly MediaTimelineSegment[],
  options: {
    width: number;
    height: number;
    viewportStart: number;
    viewportDuration: number;
    theme: DefaultTheme;
  },
) {
  const { width, height, viewportStart, viewportDuration, theme } = options;
  const gutter = MEDIA_TIMELINE_POLICY.leftGutterPx;
  const usableWidth = Math.max(0, width - gutter);
  context.clearRect(0, 0, width, height);
  const palette = new Map<
    MediaTimelineSegmentTone,
    { line: string; fill: string; activeFill: string }
  >();
  if (usableWidth === 0 || height <= 0) return palette;
  // Resolve color-mix only once per tone, not once per band in a dense frame.
  const colorsOf = (tone: MediaTimelineSegmentTone) => {
    let colors = palette.get(tone);
    if (!colors) {
      const line = resolveCanvasToken(segmentLineOf(theme, tone), context.canvas);
      context.fillStyle = `color-mix(in srgb, ${line} ${SEGMENT_FILL_PERCENT}%, transparent)`;
      const fill = context.fillStyle as string;
      context.fillStyle = `color-mix(in srgb, ${line} ${ACTIVE_SEGMENT_FILL_PERCENT}%, transparent)`;
      colors = { line, fill, activeFill: context.fillStyle as string };
      palette.set(tone, colors);
    }
    return colors;
  };
  for (const active of [false, true]) {
    for (const segment of segments) {
      if ((segment.active === true) !== active) continue;
      const x =
        gutter +
        mediaTimelineFraction(segment.startSeconds, viewportStart, viewportDuration) * usableWidth;
      const end =
        gutter +
        mediaTimelineFraction(segment.endSeconds, viewportStart, viewportDuration) * usableWidth;
      const bandWidth = end - x;
      if (bandWidth <= 0) continue;
      const colors = colorsOf(segment.tone);
      context.fillStyle = active ? colors.activeFill : colors.fill;
      context.fillRect(x, 0, bandWidth, height);
      if (active) {
        context.save();
        context.beginPath();
        context.rect(x, 0, bandWidth, height);
        context.clip();
        context.strokeStyle = colors.line;
        context.lineWidth = 1;
        context.strokeRect(x + 0.5, 0.5, Math.max(0, bandWidth - 1), Math.max(0, height - 1));
        context.restore();
      }
    }
  }
  return palette;
}
